import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  UserGroupIcon,
  BellIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface Settings {
  flywire: {
    enabled?: boolean;
    environment: 'demo' | 'live';
    demo: {
      audRecipientCode: string;
      usdRecipientCode: string;
      webhookSecret: string;
    };
    live: {
      audRecipientCode: string;
      usdRecipientCode: string;
      audWebhookSecret: string;
      usdWebhookSecret: string;
    };
  };
  paypal?: {
    enabled: boolean;
    environment: 'sandbox' | 'live';
    sandbox: {
      clientId: string;
      secretKey: string;
    };
    live: {
      clientId: string;
      secretKey: string;
    };
  };
  leadSquare: {
    accessKey: string;
    secretKey: string;
    apiUrl: string;
    enabled: boolean;
  };
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
    fromEmail: string;
    fromName: string;
    adminEmail: string;
  };
  recaptcha: {
    siteKey: string;
    secretKey: string;
    enabled: boolean;
    scoreThreshold: number;
  };
  emailNotifications?: {
    packageEnquiries: {
      enabled: boolean;
      recipients: Array<{
        email: string;
        name: string;
        active: boolean;
        _id?: string;
      }>;
    };
    contactEnquiries: {
      enabled: boolean;
      recipients: Array<{
        email: string;
        name: string;
        active: boolean;
        _id?: string;
      }>;
    };
  };
}

const AdminSettings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('flywire');
  const [testResults, setTestResults] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      await axios.put('/api/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      console.error('Error response:', error.response?.data);

      // Show detailed validation errors if available
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        // Show detailed error from backend (in development)
        toast.error(`Error: ${error.response.data.error}`);
      } else {
        toast.error(error.response?.data?.message || 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const testLeadSquared = async () => {
    try {
      setTestResults({ ...testResults, leadSquared: { testing: true } });
      const response = await axios.post('/api/settings/test-leadsquared');
      setTestResults({ ...testResults, leadSquared: { success: true, message: response.data.message } });
      toast.success('LeadSquared connection successful');
    } catch (error: any) {
      setTestResults({ ...testResults, leadSquared: { success: false, message: error.response?.data?.error || error.response?.data?.message } });
      toast.error('LeadSquared connection failed');
    }
  };

  const testSMTP = async () => {
    try {
      setTestResults({ ...testResults, smtp: { testing: true } });
      const response = await axios.post('/api/settings/test-smtp');
      setTestResults({ ...testResults, smtp: { success: true, message: response.data.message } });
      toast.success('SMTP test successful');
    } catch (error: any) {
      setTestResults({ ...testResults, smtp: { success: false, message: error.response?.data?.message } });
      toast.error('SMTP test failed');
    }
  };

  const testRecaptcha = async () => {
    try {
      setTestResults({ ...testResults, recaptcha: { testing: true } });
      // This would require actual reCAPTCHA token in real implementation
      toast('reCAPTCHA test requires frontend integration', {
        icon: 'ℹ️',
        duration: 4000
      });
    } catch (error) {
      toast.error('reCAPTCHA test failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings Not Found</h2>
          <p className="text-gray-600">Unable to load system settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
                <p className="text-gray-600">Configure Flywire, LeadSquare, SMTP and reCAPTCHA</p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'flywire', name: 'Flywire', icon: CreditCardIcon },
                { id: 'paypal', name: 'PayPal', icon: CreditCardIcon },
                { id: 'leadSquare', name: 'LeadSquare', icon: UserGroupIcon },
                { id: 'smtp', name: 'SMTP', icon: EnvelopeIcon },
                { id: 'recaptcha', name: 'reCAPTCHA', icon: ShieldCheckIcon },
                { id: 'emailNotifications', name: 'Email Notifications', icon: BellIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Flywire Tab */}
            {activeTab === 'flywire' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Flywire Payment Configuration</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure your Flywire payment gateway settings. Use Demo for testing and Live for production payments.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        checked={settings.flywire?.enabled ?? true}
                        onChange={(e) => setSettings({
                          ...settings,
                          flywire: { ...settings.flywire, enabled: e.target.checked }
                        })}
                        className="mr-2 h-4 w-4 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable Flywire Payments</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Environment *
                    </label>
                    <select
                      value={settings.flywire.environment}
                      onChange={(e) => setSettings({
                        ...settings,
                        flywire: { ...settings.flywire, environment: e.target.value as 'demo' | 'live' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="demo">Demo (Testing)</option>
                      <option value="live">Live (Production)</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {settings.flywire.environment === 'demo'
                        ? '🟡 Currently in testing mode'
                        : '🟢 Currently in production mode'}
                    </p>
                  </div>
                </div>

                {settings.flywire.environment === 'demo' ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-800 mb-3">Demo Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AUD Recipient Code
                        </label>
                        <input
                          type="text"
                          value={settings.flywire.demo.audRecipientCode}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              demo: { ...settings.flywire.demo, audRecipientCode: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          USD Recipient Code
                        </label>
                        <input
                          type="text"
                          value={settings.flywire.demo.usdRecipientCode}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              demo: { ...settings.flywire.demo, usdRecipientCode: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Webhook Secret
                        </label>
                        <input
                          type="password"
                          value={settings.flywire.demo.webhookSecret}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              demo: { ...settings.flywire.demo, webhookSecret: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-green-800">Live/Production Configuration</h4>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">PRODUCTION</span>
                    </div>
                    <p className="text-sm text-green-700 mb-4">⚠️ These settings will be used for real payment transactions.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AUD Recipient Code *
                        </label>
                        <input
                          type="text"
                          value={settings.flywire.live.audRecipientCode}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              live: { ...settings.flywire.live, audRecipientCode: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          USD Recipient Code
                        </label>
                        <input
                          type="text"
                          value={settings.flywire.live.usdRecipientCode}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              live: { ...settings.flywire.live, usdRecipientCode: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AUD Webhook Secret *
                        </label>
                        <input
                          type="password"
                          value={settings.flywire.live.audWebhookSecret}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              live: { ...settings.flywire.live, audWebhookSecret: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          USD Webhook Secret *
                        </label>
                        <input
                          type="password"
                          value={settings.flywire.live.usdWebhookSecret}
                          onChange={(e) => setSettings({
                            ...settings,
                            flywire: {
                              ...settings.flywire,
                              live: { ...settings.flywire.live, usdWebhookSecret: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PayPal Tab */}
            {activeTab === 'paypal' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">PayPal Payment Configuration</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure your PayPal payment gateway settings. Use Sandbox for testing and Live for production payments.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        checked={settings.paypal?.enabled ?? false}
                        onChange={(e) => setSettings({
                          ...settings,
                          paypal: {
                            ...settings.paypal,
                            enabled: e.target.checked,
                            environment: settings.paypal?.environment || 'sandbox',
                            sandbox: settings.paypal?.sandbox || { clientId: '', secretKey: '' },
                            live: settings.paypal?.live || { clientId: '', secretKey: '' }
                          }
                        })}
                        className="mr-2 h-4 w-4 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable PayPal Payments</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Environment *
                    </label>
                    <select
                      value={settings.paypal?.environment || 'sandbox'}
                      onChange={(e) => setSettings({
                        ...settings,
                        paypal: {
                          ...settings.paypal,
                          enabled: settings.paypal?.enabled ?? false,
                          environment: e.target.value as 'sandbox' | 'live',
                          sandbox: settings.paypal?.sandbox || { clientId: '', secretKey: '' },
                          live: settings.paypal?.live || { clientId: '', secretKey: '' }
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="sandbox">Sandbox (Testing)</option>
                      <option value="live">Live (Production)</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {settings.paypal?.environment === 'live'
                        ? '🟢 Currently in production mode'
                        : '🟡 Currently in testing mode'}
                    </p>
                  </div>
                </div>

                {settings.paypal?.environment === 'sandbox' || !settings.paypal?.environment ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-800 mb-3">Sandbox Configuration</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Client ID
                        </label>
                        <input
                          type="text"
                          value={settings.paypal?.sandbox?.clientId || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            paypal: {
                              ...settings.paypal,
                              enabled: settings.paypal?.enabled ?? false,
                              environment: settings.paypal?.environment || 'sandbox',
                              sandbox: { ...settings.paypal?.sandbox, clientId: e.target.value, secretKey: settings.paypal?.sandbox?.secretKey || '' },
                              live: settings.paypal?.live || { clientId: '', secretKey: '' }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          placeholder="Your PayPal sandbox client ID"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Secret Key
                        </label>
                        <input
                          type="password"
                          value={settings.paypal?.sandbox?.secretKey || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            paypal: {
                              ...settings.paypal,
                              enabled: settings.paypal?.enabled ?? false,
                              environment: settings.paypal?.environment || 'sandbox',
                              sandbox: { ...settings.paypal?.sandbox, clientId: settings.paypal?.sandbox?.clientId || '', secretKey: e.target.value },
                              live: settings.paypal?.live || { clientId: '', secretKey: '' }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          placeholder="••••••••••••"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-green-800">Live/Production Configuration</h4>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">PRODUCTION</span>
                    </div>
                    <p className="text-sm text-green-700 mb-4">⚠️ These settings will be used for real payment transactions.</p>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Client ID *
                        </label>
                        <input
                          type="text"
                          value={settings.paypal?.live?.clientId || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            paypal: {
                              ...settings.paypal,
                              enabled: settings.paypal?.enabled ?? false,
                              environment: settings.paypal?.environment || 'live',
                              sandbox: settings.paypal?.sandbox || { clientId: '', secretKey: '' },
                              live: { ...settings.paypal?.live, clientId: e.target.value, secretKey: settings.paypal?.live?.secretKey || '' }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          placeholder="Your PayPal live client ID"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Secret Key *
                        </label>
                        <input
                          type="password"
                          value={settings.paypal?.live?.secretKey || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            paypal: {
                              ...settings.paypal,
                              enabled: settings.paypal?.enabled ?? false,
                              environment: settings.paypal?.environment || 'live',
                              sandbox: settings.paypal?.sandbox || { clientId: '', secretKey: '' },
                              live: { ...settings.paypal?.live, clientId: settings.paypal?.live?.clientId || '', secretKey: e.target.value }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                          placeholder="••••••••••••"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">📋 How PayPal Works:</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>When enabled, customers can choose PayPal as a payment option</li>
                    <li>If only PayPal is enabled (Flywire disabled), customers go directly to PayPal</li>
                    <li>After payment, booking is created and confirmation emails are sent automatically</li>
                    <li>All transactions are visible in the Admin &gt; Bookings section</li>
                  </ul>
                </div>
              </div>
            )}

            {/* LeadSquare Tab */}
            {activeTab === 'leadSquare' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">LeadSquared Configuration</h3>
                  <button
                    onClick={testLeadSquared}
                    disabled={testResults.leadSquared?.testing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {testResults.leadSquared?.testing ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ LeadSquared is used to sync leads/enquiries. Configure your API credentials below.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        checked={settings.leadSquare.enabled}
                        onChange={(e) => setSettings({
                          ...settings,
                          leadSquare: { ...settings.leadSquare, enabled: e.target.checked }
                        })}
                        className="mr-2 h-4 w-4 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable LeadSquared Integration</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Access Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={settings.leadSquare.accessKey}
                      onChange={(e) => setSettings({
                        ...settings,
                        leadSquare: { ...settings.leadSquare, accessKey: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="e.g., u$r6e6e6a278..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secret Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={settings.leadSquare.secretKey}
                      onChange={(e) => setSettings({
                        ...settings,
                        leadSquare: { ...settings.leadSquare, secretKey: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="••••••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={settings.leadSquare.apiUrl}
                      onChange={(e) => setSettings({
                        ...settings,
                        leadSquare: { ...settings.leadSquare, apiUrl: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="https://api-in21.leadsquared.com/v2/LeadManagement.svc"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Default: https://api-in21.leadsquared.com/v2/LeadManagement.svc
                    </p>
                  </div>
                </div>

                {/* Test Result */}
                {testResults.leadSquared && !testResults.leadSquared.testing && (
                  <div className={`p-4 rounded-md ${testResults.leadSquared.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-start">
                      {testResults.leadSquared.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`font-medium ${testResults.leadSquared.success ? 'text-green-800' : 'text-red-800'}`}>
                          {testResults.leadSquared.success ? '✅ Connection Successful' : '❌ Connection Failed'}
                        </p>
                        <p className={`text-sm mt-1 ${testResults.leadSquared.success ? 'text-green-700' : 'text-red-700'}`}>
                          {testResults.leadSquared.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">📋 Required Custom Fields in LeadSquared:</h4>
                  <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                    <li><code className="bg-gray-200 px-1 rounded">mx_Mobile</code> - Phone/Mobile field</li>
                    <li><code className="bg-gray-200 px-1 rounded">mx_City</code> - Text field</li>
                    <li><code className="bg-gray-200 px-1 rounded">mx_Date_of_Birth</code> - Date field</li>
                    <li><code className="bg-gray-200 px-1 rounded">mx_Lead_Brand</code> - Text field</li>
                    <li><code className="bg-gray-200 px-1 rounded">mx_Lead_Sub_Brand</code> - Text field</li>
                    <li><code className="bg-gray-200 px-1 rounded">mx_Lead_Region</code> - Text field</li>
                    <li><code className="bg-gray-200 px-1 rounded">mx_Deal_Type</code> - Text field (NEW)</li>
                  </ul>
                </div>
              </div>
            )}

            {/* SMTP Tab */}
            {activeTab === 'smtp' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">SMTP Configuration</h3>
                  <button
                    onClick={testSMTP}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Test SMTP
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Host</label>
                    <input
                      type="text"
                      value={settings.smtp.host}
                      onChange={(e) => setSettings({
                        ...settings,
                        smtp: { ...settings.smtp, host: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
                    <input
                      type="number"
                      value={settings.smtp.port}
                      onChange={(e) => setSettings({
                        ...settings,
                        smtp: { ...settings.smtp, port: parseInt(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.smtp.secure}
                        onChange={(e) => setSettings({
                          ...settings,
                          smtp: { ...settings.smtp, secure: e.target.checked }
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Use SSL/TLS</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                    <input
                      type="email"
                      value={settings.smtp.username}
                      onChange={(e) => setSettings({
                        ...settings,
                        smtp: { ...settings.smtp, username: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                    <input
                      type="password"
                      value={settings.smtp.password}
                      onChange={(e) => setSettings({
                        ...settings,
                        smtp: { ...settings.smtp, password: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {testResults.smtp && (
                  <div className={`p-4 rounded-md ${testResults.smtp.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center">
                      {testResults.smtp.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <p className={testResults.smtp.success ? 'text-green-800' : 'text-red-800'}>
                        {testResults.smtp.message}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* reCAPTCHA Tab */}
            {activeTab === 'recaptcha' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">reCAPTCHA v3 Configuration</h3>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.recaptcha.enabled}
                      onChange={(e) => setSettings({
                        ...settings,
                        recaptcha: { ...settings.recaptcha, enabled: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Enable reCAPTCHA</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Key</label>
                    <input
                      type="text"
                      value={settings.recaptcha.siteKey}
                      onChange={(e) => setSettings({
                        ...settings,
                        recaptcha: { ...settings.recaptcha, siteKey: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your reCAPTCHA site key"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Secret Key</label>
                    <input
                      type="password"
                      value={settings.recaptcha.secretKey}
                      onChange={(e) => setSettings({
                        ...settings,
                        recaptcha: { ...settings.recaptcha, secretKey: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your reCAPTCHA secret key"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Score Threshold ({settings.recaptcha.scoreThreshold})
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.recaptcha.scoreThreshold}
                      onChange={(e) => setSettings({
                        ...settings,
                        recaptcha: { ...settings.recaptcha, scoreThreshold: parseFloat(e.target.value) }
                      })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>0.0 (Bot-like)</span>
                      <span>1.0 (Human-like)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Email Notifications Tab */}
            {activeTab === 'emailNotifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Email Notification Settings</h3>

                {/* Package Enquiries Section */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900">Package Enquiries</h4>
                      <p className="text-sm text-gray-600">Manage who receives notifications for package enquiry submissions</p>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.packageEnquiries?.enabled ?? true}
                        onChange={(e) => setSettings({
                          ...settings,
                          emailNotifications: {
                            ...settings.emailNotifications,
                            packageEnquiries: {
                              ...settings.emailNotifications?.packageEnquiries,
                              enabled: e.target.checked,
                              recipients: settings.emailNotifications?.packageEnquiries?.recipients || []
                            },
                            contactEnquiries: settings.emailNotifications?.contactEnquiries || {
                              enabled: true,
                              recipients: []
                            }
                          }
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enable Notifications</span>
                    </label>
                  </div>

                  {settings.emailNotifications?.packageEnquiries?.enabled !== false && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h5 className="text-sm font-medium text-gray-700">Recipients</h5>
                        <button
                          onClick={() => {
                            const newRecipient = { email: '', name: '', active: true };
                            setSettings({
                              ...settings,
                              emailNotifications: {
                                ...settings.emailNotifications,
                                packageEnquiries: {
                                  ...settings.emailNotifications?.packageEnquiries,
                                  enabled: settings.emailNotifications?.packageEnquiries?.enabled ?? true,
                                  recipients: [...(settings.emailNotifications?.packageEnquiries?.recipients || []), newRecipient]
                                },
                                contactEnquiries: settings.emailNotifications?.contactEnquiries || {
                                  enabled: true,
                                  recipients: []
                                }
                              }
                            });
                          }}
                          className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Recipient
                        </button>
                      </div>

                      <div className="space-y-3">
                        {(settings.emailNotifications?.packageEnquiries?.recipients || []).map((recipient, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded border">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <input
                                type="email"
                                placeholder="Email address"
                                value={recipient.email}
                                onChange={(e) => {
                                  const updatedRecipients = [...(settings.emailNotifications?.packageEnquiries?.recipients || [])];
                                  updatedRecipients[index] = { ...recipient, email: e.target.value };
                                  setSettings({
                                    ...settings,
                                    emailNotifications: {
                                      ...settings.emailNotifications,
                                      packageEnquiries: {
                                        ...settings.emailNotifications?.packageEnquiries,
                                        enabled: settings.emailNotifications?.packageEnquiries?.enabled ?? true,
                                        recipients: updatedRecipients
                                      },
                                      contactEnquiries: settings.emailNotifications?.contactEnquiries || {
                                        enabled: true,
                                        recipients: []
                                      }
                                    }
                                  });
                                }}
                                className="px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                              <input
                                type="text"
                                placeholder="Name (optional)"
                                value={recipient.name}
                                onChange={(e) => {
                                  const updatedRecipients = [...(settings.emailNotifications?.packageEnquiries?.recipients || [])];
                                  updatedRecipients[index] = { ...recipient, name: e.target.value };
                                  setSettings({
                                    ...settings,
                                    emailNotifications: {
                                      ...settings.emailNotifications,
                                      packageEnquiries: {
                                        ...settings.emailNotifications?.packageEnquiries,
                                        enabled: settings.emailNotifications?.packageEnquiries?.enabled ?? true,
                                        recipients: updatedRecipients
                                      },
                                      contactEnquiries: settings.emailNotifications?.contactEnquiries || {
                                        enabled: true,
                                        recipients: []
                                      }
                                    }
                                  });
                                }}
                                className="px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={recipient.active}
                                onChange={(e) => {
                                  const updatedRecipients = [...(settings.emailNotifications?.packageEnquiries?.recipients || [])];
                                  updatedRecipients[index] = { ...recipient, active: e.target.checked };
                                  setSettings({
                                    ...settings,
                                    emailNotifications: {
                                      ...settings.emailNotifications,
                                      packageEnquiries: {
                                        ...settings.emailNotifications?.packageEnquiries,
                                        enabled: settings.emailNotifications?.packageEnquiries?.enabled ?? true,
                                        recipients: updatedRecipients
                                      },
                                      contactEnquiries: settings.emailNotifications?.contactEnquiries || {
                                        enabled: true,
                                        recipients: []
                                      }
                                    }
                                  });
                                }}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="ml-2 text-sm text-gray-600">Active</span>
                            </label>
                            <button
                              onClick={() => {
                                const updatedRecipients = (settings.emailNotifications?.packageEnquiries?.recipients || []).filter((_, i) => i !== index);
                                setSettings({
                                  ...settings,
                                  emailNotifications: {
                                    ...settings.emailNotifications,
                                    packageEnquiries: {
                                      ...settings.emailNotifications?.packageEnquiries,
                                      enabled: settings.emailNotifications?.packageEnquiries?.enabled ?? true,
                                      recipients: updatedRecipients
                                    },
                                    contactEnquiries: settings.emailNotifications?.contactEnquiries || {
                                      enabled: true,
                                      recipients: []
                                    }
                                  }
                                });
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Enquiries Section */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-semibold text-gray-900">Contact Form Enquiries</h4>
                      <p className="text-sm text-gray-600">Manage who receives notifications for contact form submissions</p>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.contactEnquiries?.enabled ?? true}
                        onChange={(e) => setSettings({
                          ...settings,
                          emailNotifications: {
                            ...settings.emailNotifications,
                            contactEnquiries: {
                              ...settings.emailNotifications?.contactEnquiries,
                              enabled: e.target.checked,
                              recipients: settings.emailNotifications?.contactEnquiries?.recipients || []
                            },
                            packageEnquiries: settings.emailNotifications?.packageEnquiries || {
                              enabled: true,
                              recipients: []
                            }
                          }
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enable Notifications</span>
                    </label>
                  </div>

                  {settings.emailNotifications?.contactEnquiries?.enabled !== false && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h5 className="text-sm font-medium text-gray-700">Recipients</h5>
                        <button
                          onClick={() => {
                            const newRecipient = { email: '', name: '', active: true };
                            setSettings({
                              ...settings,
                              emailNotifications: {
                                ...settings.emailNotifications,
                                contactEnquiries: {
                                  ...settings.emailNotifications?.contactEnquiries,
                                  enabled: settings.emailNotifications?.contactEnquiries?.enabled ?? true,
                                  recipients: [...(settings.emailNotifications?.contactEnquiries?.recipients || []), newRecipient]
                                },
                                packageEnquiries: settings.emailNotifications?.packageEnquiries || {
                                  enabled: true,
                                  recipients: []
                                }
                              }
                            });
                          }}
                          className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Recipient
                        </button>
                      </div>

                      <div className="space-y-3">
                        {(settings.emailNotifications?.contactEnquiries?.recipients || []).map((recipient, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded border">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <input
                                type="email"
                                placeholder="Email address"
                                value={recipient.email}
                                onChange={(e) => {
                                  const updatedRecipients = [...(settings.emailNotifications?.contactEnquiries?.recipients || [])];
                                  updatedRecipients[index] = { ...recipient, email: e.target.value };
                                  setSettings({
                                    ...settings,
                                    emailNotifications: {
                                      ...settings.emailNotifications,
                                      contactEnquiries: {
                                        ...settings.emailNotifications?.contactEnquiries,
                                        enabled: settings.emailNotifications?.contactEnquiries?.enabled ?? true,
                                        recipients: updatedRecipients
                                      },
                                      packageEnquiries: settings.emailNotifications?.packageEnquiries || {
                                        enabled: true,
                                        recipients: []
                                      }
                                    }
                                  });
                                }}
                                className="px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                              <input
                                type="text"
                                placeholder="Name (optional)"
                                value={recipient.name}
                                onChange={(e) => {
                                  const updatedRecipients = [...(settings.emailNotifications?.contactEnquiries?.recipients || [])];
                                  updatedRecipients[index] = { ...recipient, name: e.target.value };
                                  setSettings({
                                    ...settings,
                                    emailNotifications: {
                                      ...settings.emailNotifications,
                                      contactEnquiries: {
                                        ...settings.emailNotifications?.contactEnquiries,
                                        enabled: settings.emailNotifications?.contactEnquiries?.enabled ?? true,
                                        recipients: updatedRecipients
                                      },
                                      packageEnquiries: settings.emailNotifications?.packageEnquiries || {
                                        enabled: true,
                                        recipients: []
                                      }
                                    }
                                  });
                                }}
                                className="px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={recipient.active}
                                onChange={(e) => {
                                  const updatedRecipients = [...(settings.emailNotifications?.contactEnquiries?.recipients || [])];
                                  updatedRecipients[index] = { ...recipient, active: e.target.checked };
                                  setSettings({
                                    ...settings,
                                    emailNotifications: {
                                      ...settings.emailNotifications,
                                      contactEnquiries: {
                                        ...settings.emailNotifications?.contactEnquiries,
                                        enabled: settings.emailNotifications?.contactEnquiries?.enabled ?? true,
                                        recipients: updatedRecipients
                                      },
                                      packageEnquiries: settings.emailNotifications?.packageEnquiries || {
                                        enabled: true,
                                        recipients: []
                                      }
                                    }
                                  });
                                }}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="ml-2 text-sm text-gray-600">Active</span>
                            </label>
                            <button
                              onClick={() => {
                                const updatedRecipients = (settings.emailNotifications?.contactEnquiries?.recipients || []).filter((_, i) => i !== index);
                                setSettings({
                                  ...settings,
                                  emailNotifications: {
                                    ...settings.emailNotifications,
                                    contactEnquiries: {
                                      ...settings.emailNotifications?.contactEnquiries,
                                      enabled: settings.emailNotifications?.contactEnquiries?.enabled ?? true,
                                      recipients: updatedRecipients
                                    },
                                    packageEnquiries: settings.emailNotifications?.packageEnquiries || {
                                      enabled: true,
                                      recipients: []
                                    }
                                  }
                                });
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <BellIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Email Notification Info
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Only active recipients will receive email notifications</li>
                          <li>Package enquiries are sent when users submit enquiry forms on package detail pages</li>
                          <li>Contact enquiries are sent when users submit the contact form</li>
                          <li>Make sure SMTP settings are configured correctly for emails to be sent</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
